import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Download, 
  RefreshCw, 
  Plus, 
  MapPin, 
  Building, 
  Users, 
  Settings,
  CheckCircle,
  AlertCircle,
  BarChart3,
  Database,
  Eye,
  Edit,
  Trash2,
  Building2,
  Globe,
  Map,
  TreePine,
  Navigation
} from 'lucide-react';

// Import the API service and types
import { territorialAPI } from '../services/territorial-api.service';
import {
  CETerritory,
  Circle,
  District,
  Division,
  SubDivision,
  Mandal,
  TerritorialStatsDto,
  SearchResultsDto,
  PaginationResponseDto,
  QueryOptionsDto
} from '../types/territorial.types';

// Component Props Interfaces
interface StatCardProps {
  title: string;
  total: number;
  active: number;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'indigo';
}

interface TableProps {
  showStats?: boolean;
}

interface CETerritoryTableProps extends TableProps {
  territories: CETerritory[];
  onEdit: (territory: CETerritory) => void;
  onDelete: (id: number) => void;
  onView: (territory: CETerritory) => void;
}

interface CircleTableProps extends TableProps {
  circles: Circle[];
  onEdit: (circle: Circle) => void;
  onDelete: (id: number) => void;
  onView: (circle: Circle) => void;
}

interface DistrictTableProps extends TableProps {
  districts: District[];
  onEdit: (district: District) => void;
  onDelete: (id: number) => void;
  onView: (district: District) => void;
}

interface DivisionTableProps extends TableProps {
  divisions: Division[];
  onEdit: (division: Division) => void;
  onDelete: (id: number) => void;
  onView: (division: Division) => void;
}

interface SubDivisionTableProps extends TableProps {
  subDivisions: SubDivision[];
  onEdit: (subDivision: SubDivision) => void;
  onDelete: (id: number) => void;
  onView: (subDivision: SubDivision) => void;
}

interface MandalTableProps extends TableProps {
  mandals: Mandal[];
  onEdit: (mandal: Mandal) => void;
  onDelete: (id: number) => void;
  onView: (mandal: Mandal) => void;
}

interface SearchResultsProps {
  results: SearchResultsDto;
}

// Main Component
export default function TerritorialManagementPage() {
  const [activeTab, setActiveTab] = useState('ce-territories');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [ceTerritories, setCETerritories] = useState<PaginationResponseDto<CETerritory> | null>(null);
  const [circles, setCircles] = useState<PaginationResponseDto<Circle> | null>(null);
  const [districts, setDistricts] = useState<PaginationResponseDto<District> | null>(null);
  const [divisions, setDivisions] = useState<PaginationResponseDto<Division> | null>(null);
  const [subDivisions, setSubDivisions] = useState<PaginationResponseDto<SubDivision> | null>(null);
  const [mandals, setMandals] = useState<PaginationResponseDto<Mandal> | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResultsDto | null>(null);
  const [stats, setStats] = useState<TerritorialStatsDto | null>(null);
  
  // Filters
  const [selectedCETerritory, setSelectedCETerritory] = useState('');
  const [selectedCircle, setSelectedCircle] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedSubDivision, setSelectedSubDivision] = useState('');
  const [showStats, setShowStats] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadTabData();
  }, [activeTab, selectedCETerritory, selectedCircle, selectedDistrict, selectedDivision, selectedSubDivision, currentPage, pageSize]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [territoriesData, statsData] = await Promise.all([
        territorialAPI.getCETerritories({ page: 1, limit: 100 }),
        territorialAPI.getTerritorialStats()
      ]);
      setCETerritories(territoriesData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const loadTabData = async () => {
    try {
      setLoading(true);
      const queryOptions: QueryOptionsDto = {
        page: currentPage,
        limit: pageSize,
        includeRelations: true
      };

      switch (activeTab) {
        case 'ce-territories':
          const territoryData = await territorialAPI.getCETerritories(queryOptions);
          setCETerritories(territoryData);
          break;
        case 'circles':
          if (selectedCETerritory) {
            const circleData = await territorialAPI.getCirclesByCETerritory(Number(selectedCETerritory), queryOptions);
            setCircles(circleData);
          } else {
            const circleData = await territorialAPI.getCircles(queryOptions);
            setCircles(circleData);
          }
          break;
        case 'districts':
          if (selectedCircle) {
            const districtData = await territorialAPI.getDistrictsByCircle(Number(selectedCircle), queryOptions);
            setDistricts(districtData);
          } else {
            const districtData = await territorialAPI.getDistricts(queryOptions);
            setDistricts(districtData);
          }
          break;
        case 'divisions':
          if (selectedDistrict) {
            const divisionData = await territorialAPI.getDivisionsByDistrict(Number(selectedDistrict), queryOptions);
            setDivisions(divisionData);
          } else {
            const divisionData = await territorialAPI.getDivisions(queryOptions);
            setDivisions(divisionData);
          }
          break;
        case 'sub-divisions':
          if (selectedDivision) {
            const subDivisionData = await territorialAPI.getSubDivisionsByDivision(Number(selectedDivision), queryOptions);
            setSubDivisions(subDivisionData);
          } else {
            const subDivisionData = await territorialAPI.getSubDivisions(queryOptions);
            setSubDivisions(subDivisionData);
          }
          break;
        case 'mandals':
          if (selectedSubDivision) {
            const mandalData = await territorialAPI.getMandalsBySubDivision(Number(selectedSubDivision), queryOptions);
            setMandals(mandalData);
          } else {
            const mandalData = await territorialAPI.getMandals(queryOptions);
            setMandals(mandalData);
          }
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    try {
      setLoading(true);
      const results = await territorialAPI.searchTerritorial(searchTerm);
      setSearchResults(results);
      setActiveTab('search');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const blob = await territorialAPI.exportTerritorialData();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'territorial-data.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    try {
      setLoading(true);
      await territorialAPI.seedTerritorialData();
      await loadInitialData();
      alert('Territorial data seeded successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seeding failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: any) => {
    console.log('Edit:', item);
    // Implement edit functionality
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      setLoading(true);
      switch (activeTab) {
        case 'ce-territories':
          await territorialAPI.deleteCETerritory(id);
          break;
        case 'circles':
          await territorialAPI.deleteCircle(id);
          break;
        case 'districts':
          await territorialAPI.deleteDistrict(id);
          break;
        case 'divisions':
          await territorialAPI.deleteDivision(id);
          break;
        case 'sub-divisions':
          await territorialAPI.deleteSubDivision(id);
          break;
        case 'mandals':
          await territorialAPI.deleteMandal(id);
          break;
      }
      await loadTabData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (item: any) => {
    console.log('View:', item);
    // Implement view functionality
  };

  const tabs = [
    { id: 'ce-territories', label: 'CE Territories', icon: Globe, count: stats?.ceTerritories?.total },
    { id: 'circles', label: 'Circles', icon: Building2, count: stats?.circles?.total },
    { id: 'districts', label: 'Districts', icon: MapPin, count: stats?.districts?.total },
    { id: 'divisions', label: 'Divisions', icon: Building, count: stats?.divisions?.total },
    { id: 'sub-divisions', label: 'Sub Divisions', icon: TreePine, count: stats?.subDivisions?.total },
    { id: 'mandals', label: 'Mandals', icon: Navigation, count: stats?.mandals?.total },
    { id: 'search', label: 'Search', icon: Search, count: null }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Database className="text-blue-600" />
              Territorial Management System
            </h1>
            <div className="flex gap-2">
              <button
                onClick={() => setShowStats(!showStats)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  showStats ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <BarChart3 size={16} />
                {showStats ? 'Hide Stats' : 'Show Stats'}
              </button>
              <button
                onClick={handleExport}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Download size={16} />
                Export
              </button>
              <button
                onClick={handleSeedData}
                disabled={loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Plus size={16} />
                Seed Data
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search territories, circles, districts, divisions, sub-divisions, mandals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Hierarchical Filters */}
            {ceTerritories && (
              <select
                value={selectedCETerritory}
                onChange={(e) => {
                  setSelectedCETerritory(e.target.value);
                  setSelectedCircle('');
                  setSelectedDistrict('');
                  setSelectedDivision('');
                  setSelectedSubDivision('');
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All CE Territories</option>
                {ceTerritories.data.map((territory) => (
                  <option key={territory.id} value={territory.id}>
                    {territory.name}
                  </option>
                ))}
              </select>
            )}

            {circles && selectedCETerritory && (
              <select
                value={selectedCircle}
                onChange={(e) => {
                  setSelectedCircle(e.target.value);
                  setSelectedDistrict('');
                  setSelectedDivision('');
                  setSelectedSubDivision('');
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Circles</option>
                {circles.data.filter(c => c.ceTerritoryId === Number(selectedCETerritory)).map((circle) => (
                  <option key={circle.id} value={circle.id}>
                    {circle.name}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={handleSearch}
              disabled={loading || !searchTerm.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Search size={16} />
              Search
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
            <AlertCircle className="text-red-500" size={20} />
            <span className="text-red-700">{error}</span>
            <button 
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
            <StatCard 
              title="CE Territories" 
              total={stats.ceTerritories.total} 
              active={stats.ceTerritories.active}
              icon={Globe}
              color="blue"
            />
            <StatCard 
              title="Circles" 
              total={stats.circles.total} 
              active={stats.circles.active}
              icon={Building2}
              color="green"
            />
            <StatCard 
              title="Districts" 
              total={stats.districts.total} 
              active={stats.districts.active}
              icon={MapPin}
              color="purple"
            />
            <StatCard 
              title="Divisions" 
              total={stats.divisions.total} 
              active={stats.divisions.active}
              icon={Building}
              color="orange"
            />
            <StatCard 
              title="Sub Divisions" 
              total={stats.subDivisions.total} 
              active={stats.subDivisions.active}
              icon={TreePine}
              color="red"
            />
            <StatCard 
              title="Mandals" 
              total={stats.mandals.total} 
              active={stats.mandals.active}
              icon={Navigation}
              color="indigo"
            />
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6 overflow-x-auto">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={16} />
                      {tab.label}
                      {tab.count !== null && tab.count !== undefined && (
                        <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                          {tab.count}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="animate-spin text-blue-600" size={24} />
                <span className="ml-2 text-gray-600">Loading...</span>
              </div>
            )}

            {!loading && activeTab === 'ce-territories' && ceTerritories && (
              <CETerritoryTable 
                territories={ceTerritories.data} 
                showStats={showStats}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
            )}

            {!loading && activeTab === 'circles' && circles && (
              <CircleTable 
                circles={circles.data} 
                showStats={showStats}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
            )}

            {!loading && activeTab === 'districts' && districts && (
              <DistrictTable 
                districts={districts.data} 
                showStats={showStats}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
            )}

            {!loading && activeTab === 'divisions' && divisions && (
              <DivisionTable 
                divisions={divisions.data} 
                showStats={showStats}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
            )}

            {!loading && activeTab === 'sub-divisions' && subDivisions && (
              <SubDivisionTable 
                subDivisions={subDivisions.data} 
                showStats={showStats}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
            )}

            {!loading && activeTab === 'mandals' && mandals && (
              <MandalTable 
                mandals={mandals.data} 
                showStats={showStats}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
            )}

            {!loading && activeTab === 'search' && searchResults && (
              <SearchResults results={searchResults} />
            )}

            {/* Pagination */}
            {!loading && activeTab !== 'search' && (
              <Pagination 
                currentData={
                  activeTab === 'ce-territories' ? ceTerritories :
                  activeTab === 'circles' ? circles :
                  activeTab === 'districts' ? districts :
                  activeTab === 'divisions' ? divisions :
                  activeTab === 'sub-divisions' ? subDivisions :
                  activeTab === 'mandals' ? mandals : null
                }
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, total, active, icon: Icon, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{total}</p>
          <p className="text-sm text-gray-500">{active} active</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}

// CE Territory Table
function CETerritoryTable({ territories, showStats, onEdit, onDelete, onView }: CETerritoryTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {territories.map((territory) => (
            <tr key={territory.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {territory.code}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {territory.name}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                {territory.description || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  territory.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <CheckCircle size={12} className="mr-1" />
                  {territory.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(territory.createdAt).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex gap-2">
                  <button 
                    onClick={() => onView(territory)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Eye size={16} />
                  </button>
                  <button 
                    onClick={() => onEdit(territory)}
                    className="text-gray-600 hover:text-gray-800"
  >
                    <Edit size={16} />
                  </button>
                  <button 
                    onClick={() => onDelete(territory.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Circle Table
function CircleTable({ circles, showStats, onEdit, onDelete, onView }: CircleTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CE Territory</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {circles.map((circle) => (
            <tr key={circle.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {circle.code}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {circle.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {circle.ceTerritory?.name || `Territory ${circle.ceTerritoryId}`}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                {circle.description || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  circle.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <CheckCircle size={12} className="mr-1" />
                  {circle.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex gap-2">
                  <button 
                    onClick={() => onView(circle)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Eye size={16} />
                  </button>
                  <button 
                    onClick={() => onEdit(circle)}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    onClick={() => onDelete(circle.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// District Table
function DistrictTable({ districts, showStats, onEdit, onDelete, onView }: DistrictTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Circle</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Headquarters</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Population</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Area (sq km)</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {districts.map((district) => (
            <tr key={district.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {district.code}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {district.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {district.circle?.name || `Circle ${district.circleId}`}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {district.headquarters || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {district.population ? district.population.toLocaleString('en-IN') : '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {district.area ? district.area.toFixed(2) : '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  district.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <CheckCircle size={12} className="mr-1" />
                  {district.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex gap-2">
                  <button 
                    onClick={() => onView(district)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Eye size={16} />
                  </button>
                  <button 
                    onClick={() => onEdit(district)}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    onClick={() => onDelete(district.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Division Table
function DivisionTable({ divisions, showStats, onEdit, onDelete, onView }: DivisionTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Headquarters</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {divisions.map((division) => (
            <tr key={division.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {division.code}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {division.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {division.district?.name || `District ${division.districtId}`}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {division.headquarters || '-'}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                {division.description || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  division.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <CheckCircle size={12} className="mr-1" />
                  {division.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex gap-2">
                  <button 
                    onClick={() => onView(division)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Eye size={16} />
                  </button>
                  <button 
                    onClick={() => onEdit(division)}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    onClick={() => onDelete(division.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Sub Division Table
function SubDivisionTable({ subDivisions, showStats, onEdit, onDelete, onView }: SubDivisionTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Division</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Headquarters</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Special Notes</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {subDivisions.map((subDivision) => (
            <tr key={subDivision.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {subDivision.code}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {subDivision.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {subDivision.division?.name || `Division ${subDivision.divisionId}`}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {subDivision.headquarters || '-'}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                {subDivision.specialNotes || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  subDivision.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <CheckCircle size={12} className="mr-1" />
                  {subDivision.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex gap-2">
                  <button 
                    onClick={() => onView(subDivision)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Eye size={16} />
                  </button>
                  <button 
                    onClick={() => onEdit(subDivision)}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    onClick={() => onDelete(subDivision.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Mandal Table
function MandalTable({ mandals, showStats, onEdit, onDelete, onView }: MandalTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sub Division</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Headquarters</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Population</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Villages</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PIN Code</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {mandals.map((mandal) => (
            <tr key={mandal.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {mandal.code}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {mandal.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {mandal.subDivision?.name || `Sub Division ${mandal.subDivisionId}`}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {mandal.headquarters || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {mandal.population ? mandal.population.toLocaleString('en-IN') : '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {mandal.villages || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                {mandal.pinCode || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  mandal.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <CheckCircle size={12} className="mr-1" />
                  {mandal.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex gap-2">
                  <button 
                    onClick={() => onView(mandal)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Eye size={16} />
                  </button>
                  <button 
                    onClick={() => onEdit(mandal)}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    onClick={() => onDelete(mandal.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Search Results Component
function SearchResults({ results }: SearchResultsProps) {
  const { ceTerritories, circles, districts, divisions, subDivisions, mandals } = results;
  const totalResults = ceTerritories.length + circles.length + districts.length + divisions.length + subDivisions.length + mandals.length;

  if (totalResults === 0) {
    return (
      <div className="text-center py-12">
        <Search className="mx-auto text-gray-400 mb-4" size={48} />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
        <p className="text-gray-500">Try adjusting your search terms or filters.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Search Results ({totalResults} found)
        </h3>
      </div>

      {ceTerritories.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Globe size={16} />
            CE Territories ({ceTerritories.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ceTerritories.map(territory => (
              <div key={territory.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-blue-900">{territory.name}</h5>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {territory.code}
                  </span>
                </div>
                <p className="text-sm text-blue-700">{territory.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {circles.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Building2 size={16} />
            Circles ({circles.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {circles.map(circle => (
              <div key={circle.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-green-900">{circle.name}</h5>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    {circle.code}
                  </span>
                </div>
                <p className="text-sm text-green-700">{circle.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {districts.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2">
            <MapPin size={16} />
            Districts ({districts.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {districts.map(district => (
              <div key={district.id} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-purple-900">{district.name}</h5>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                    {district.code}
                  </span>
                </div>
                <p className="text-sm text-purple-700">HQ: {district.headquarters}</p>
                {district.population && (
                  <p className="text-sm text-purple-700">Population: {district.population.toLocaleString('en-IN')}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {divisions.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Building size={16} />
            Divisions ({divisions.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {divisions.map(division => (
              <div key={division.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-orange-900">{division.name}</h5>
                  <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                    {division.code}
                  </span>
                </div>
                <p className="text-sm text-orange-700">HQ: {division.headquarters}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {subDivisions.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2">
            <TreePine size={16} />
            Sub Divisions ({subDivisions.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subDivisions.map(subDivision => (
              <div key={subDivision.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-red-900">{subDivision.name}</h5>
                  <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                    {subDivision.code}
                  </span>
                </div>
                <p className="text-sm text-red-700">HQ: {subDivision.headquarters}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {mandals.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Navigation size={16} />
            Mandals ({mandals.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mandals.map(mandal => (
              <div key={mandal.id} className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-indigo-900">{mandal.name}</h5>
                  <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                    {mandal.code}
                  </span>
                </div>
                <p className="text-sm text-indigo-700">HQ: {mandal.headquarters}</p>
                {mandal.villages && (
                  <p className="text-sm text-indigo-700">Villages: {mandal.villages}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Pagination Component
function Pagination({ currentData, onPageChange, onPageSizeChange }: {
  currentData: PaginationResponseDto<any> | null;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) {
  if (!currentData || currentData.totalPages <= 1) return null;

  const { currentPage, totalPages, totalCount, limit } = currentData;

  return (
    <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-500">
          Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalCount)} of {totalCount} results
        </span>
        <select
          value={limit}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="px-3 py-1 border border-gray-300 rounded text-sm"
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!currentData.hasPreviousPage}
          className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Previous
        </button>
        
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`px-3 py-1 border border-gray-300 rounded text-sm ${
                page === currentPage 
                  ? 'bg-blue-600 text-white border-blue-600' 
                  : 'hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          );
        })}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!currentData.hasNextPage}
          className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}